<template>
  <div
    class="bg-white border border-truegray-300 rounded-xl relative w-full hover:shadow transition-all duration-300 ease-in-out flex flex-col h-full"
  >
    <div class="flex w-full p-6 lg:pb-4 flex-wrap">
      <div
        class="w-full m-auto mb-6 lg:m-0 lg:w-1/3 flex-shrink-0 flex flex-col items-center justify-center pr-2"
        :style="{ maxWidth: '125px' }"
      >
        <ProfileImage :connection="connection" />
        <div
          class="py-1 px-2 text-xs text-white rounded-xl relative right-3 ml-4 mt-4"
          :class="[
            connection.local ? 'bg-primary' : 'cw-gradient',
            connection.disabled ? 'opacity-60' : '',
          ]"
          :style="{ maxWidth: 'max-content' }"
        >
          <span v-if="connection.local">Local </span>
          <span v-else>CareerWise </span>
        </div>
      </div>

      <div
        class="flex-grow flex-shrink-0 w-full lg:w-2/3 flex flex-col lg:pl-4"
      >
        <div class="mb-4">
          <div
            class="flex flex-row justify-between"
            :class="connection.disabled ? 'opacity-60' : ''"
          >
            <div>
              <h3 class="text-xl mb-1">{{ connection.name }}</h3>
              <p class="text-xs text-truegray-500">
                {{ connection.company }}
              </p>
            </div>
            <div v-if="canShowAdminMenu" class="flex-shrink-0 ml-2">
              <CardConnectionAdminMenu
                :connection="connection"
                :context="context"
              />
            </div>
          </div>
        </div>
        <div>
          <div
            class="flex space-x-2 items-center"
            :class="connection.disabled ? 'opacity-60' : ''"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-4 h-4"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z"
              />
            </svg>

            <p class="text-truegray-700">{{ connection.job_title }}</p>
          </div>
          <div
            v-if="connection.region"
            class="flex space-x-2 items-center"
            :class="connection.disabled ? 'opacity-60' : ''"
          >
            <img
              class="mx-1"
              src="/img/connections/icons/region.svg"
              alt="Region"
            />
            <p class="text-truegray-700 mr-4">{{ connection.region.title }}</p>
          </div>
          <div v-if="!!connection.tags_regions?.length">
            <MultipleTags
              :is-regions="true"
              :tags="connection.tags_regions.map((i) => i.title)"
              :colour="'text-truegray-700'"
              :titles="true"
              :flex="false"
              :font-heading="false"
              :truncate="true"
              :disabled="connection.disabled"
            />
          </div>
          <div v-if="!!connection.tags_industries?.length">
            <MultipleTags
              :tags="connection.tags_industries.map((i) => i.title)"
              :colour="'text-truegray-700'"
              :titles="true"
              :flex="false"
              :font-heading="false"
              :truncate="true"
              :disabled="connection.disabled"
            />
          </div>
        </div>
      </div>
    </div>
    <!-- Action buttons -->
    <div class="border-t border-truegray-300 w-full mt-auto">
      <div class="flex w-full">
        <button
          v-for="(action, index) in actions"
          :key="action.name"
          :disabled="loadingStates[action.name]"
          class="flex items-center justify-center py-4 space-x-4 hover:text-primary hover:shadow-inner group transition-all text-truegray-700"
          :class="[
            action.isRemoveAction
              ? 'text-red-400 hover:text-red-600'
              : connection.local
              ? 'hover:text-primary'
              : 'hover:text-cw-700',
            actions.length < 2
              ? 'w-full'
              : 'w-1/2 border-truegray-100 border-r',
            { 'rounded-bl-xl': index === 0 },
            { 'rounded-br-xl': index === actions.length - 1 },
          ]"
          title="View connection"
          @click="action.callback(connection.id)"
        >
          <div
            v-if="!loadingStates[action.name]"
            class="flex items-center justify-center space-x-4"
          >
            <span v-html="action.svg" />
            <p
              :class="
                action.isRemoveAction
                  ? 'text-red-400 group-hover:text-red-600 min-content-small-screen'
                  : connection.local
                  ? 'group-hover:text-primary'
                  : 'group-hover:text-cw-700'
              "
            >
              {{ action.title }}
            </p>
          </div>
          <div
            v-else
            class="w-full h-5 flex items-center justify-center space-x-4"
          >
            <lottie :options="loadingAnimationOptions" />
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref } from 'vue'
import { useRoute } from 'vue-router/composables'
import { useStore } from '../../../../js/store/index.js'
import { EventBus } from '../../../../js/plugins/event-bus.js'
import * as loadingAnimation from '../../../../shared/components/loading-dark.json'
import CardConnectionAdminMenu from './CardConnectionAdminMenu.vue'
import ProfileImage from './ImageConnection.vue'
import MultipleTags from '../../../../shared/components/MultipleTags.vue'

export default defineComponent({
  name: 'CardConnection',
  components: { ProfileImage, MultipleTags, CardConnectionAdminMenu },

  props: {
    connection: {
      type: Object,
      required: true,
    },

    context: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    const store = useStore()
    const route = useRoute()

    const user = computed(() => store.getters['shared/getUser'])
    const isAdmin = computed(() => store.getters['shared/getUserAccess'])
    const isContributor = computed(() => store.getters['shared/isContributor'])
    const isPublicProfileContext = computed(() =>
      store.getters['contributors/isPublicProfileContext'](route),
    )
    const isManagerContext = computed(() =>
      store.getters['connections/isManagerContext'](route.fullPath),
    )

    const context = computed(() => {
      if (isPublicProfileContext.value) {
        return 'CONTRIBUTOR_PUBLIC'
      }

      if (isContributor.value) {
        return 'CONTRIBUTOR_PORTAL'
      }

      return 'TENANT'
    })

    const ACTION_MAP = {
      CONTRIBUTOR_PORTAL: 'contributors/dispatchUpdateConnection',
      CONTRIBUTOR_PUBLIC: 'contributors/dispatchUpdateProfileConnection',
      TENANT: 'connections/dispatchUpdateConnection',
    }

    const actions = computed(() => {
      const allActions = [
        {
          name: 'view',
          title: 'View Profile',
          svg: `<svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M7 0.666016C3.41602 0.666016 0.5 3.58203 0.5 7.16602C0.5 10.75 3.41602 13.666 7 13.666C10.584 13.666 13.5 10.75 13.5 7.16602C13.5 3.58203 10.584 0.666016 7 0.666016ZM7 1.66602C10.043 1.66602 12.5 4.12305 12.5 7.16602C12.5 10.209 10.043 12.666 7 12.666C3.95703 12.666 1.5 10.209 1.5 7.16602C1.5 4.12305 3.95703 1.66602 7 1.66602ZM7 3.16602C5.625 3.16602 4.5 4.29102 4.5 5.66602C4.5 6.42383 4.85352 7.09766 5.39062 7.55664C4.26562 8.14062 3.5 9.31641 3.5 10.666H4.5C4.5 9.2793 5.61328 8.16602 7 8.16602C8.38672 8.16602 9.5 9.2793 9.5 10.666H10.5C10.5 9.31641 9.73438 8.14062 8.60938 7.55664C9.14648 7.09766 9.5 6.42383 9.5 5.66602C9.5 4.29102 8.375 3.16602 7 3.16602ZM7 4.16602C7.83398 4.16602 8.5 4.83203 8.5 5.66602C8.5 6.5 7.83398 7.16602 7 7.16602C6.16602 7.16602 5.5 6.5 5.5 5.66602C5.5 4.83203 6.16602 4.16602 7 4.16602Z"/></svg>`,
          callback: () => {
            showModal()
          },

          hideAction: false,
        },
        {
          name: 'save',
          title: 'Save Connection',
          svg: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M6 6.878V6a2.25 2.25 0 012.25-2.25h7.5A2.25 2.25 0 0118 6v.878m-12 0c.235-.083.487-.128.75-.128h10.5c.263 0 .515.045.75.128m-12 0A2.25 2.25 0 004.5 9v.878m13.5-3A2.25 2.25 0 0119.5 9v.878m0 0a2.246 2.246 0 00-.75-.128H5.25c-.263 0-.515.045-.75.128m15 0A2.25 2.25 0 0121 12v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6c0-.98.626-1.813 1.5-2.122"/></svg>`,
          callback: (id) => {
            saveConnection(id)
          },

          hideAction:
            props.context === 'CW' ||
            !isManagerContext.value ||
            isSavedConnection.value ||
            !isAdmin.value ||
            isContributor.value,
        },
        {
          name: 'unsave',
          title: 'Unsave Connection',
          svg: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/></svg>`,
          callback: (id) => {
            unsaveConnection(id)
          },

          hideAction:
            props.context === 'CW' ||
            !isManagerContext.value ||
            !isSavedConnection.value ||
            !isAdmin.value ||
            isContributor.value,
          isRemoveAction: true,
        },
        {
          name: 'enable',
          title: 'Enable Connection',
          svg: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M6 6.878V6a2.25 2.25 0 012.25-2.25h7.5A2.25 2.25 0 0118 6v.878m-12 0c.235-.083.487-.128.75-.128h10.5c.263 0 .515.045.75.128m-12 0A2.25 2.25 0 004.5 9v.878m13.5-3A2.25 2.25 0 0119.5 9v.878m0 0a2.246 2.246 0 00-.75-.128H5.25c-.263 0-.515.045-.75.128m15 0A2.25 2.25 0 0121 12v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6c0-.98.626-1.813 1.5-2.122"/></svg>`,
          callback: (id) => {
            enableConnection(id)
          },

          hideAction:
            props.context !== 'CW' ||
            !isManagerContext.value ||
            !props.connection.disabled,
        },
        {
          name: 'disable',
          title: 'Disable Connection',
          svg: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/></svg>`,
          callback: (id) => {
            disableConnection(id)
          },

          hideAction:
            props.context !== 'CW' ||
            props.connection.disabled ||
            !isManagerContext.value,
          isRemoveAction: true,
        },
      ]

      return allActions.filter((action) => !action.hideAction)
    })

    const isSavedConnection = computed(() => props.context === 'SAVED')
    const isOrganisationConnection = computed(
      () => props.connection.organisation_id,
    )

    const canShowAdminMenu = computed(() => {
      if (isPublicProfileContext.value) {
        return false
      }

      if (isContributor.value) {
        return true
      }

      if (isAdmin.value) {
        return !isOrganisationConnection.value && isManagerContext.value
      }

      return false
    })

    const showModal = async () => {
      const action = ACTION_MAP[context.value]
      const actionData = { connectionId: props.connection.id }
      if (!isContributor.value) {
        actionData.data = {
          user: user.value,
        }
      }
      const connectionData = await store.dispatch(action, actionData)

      EventBus.$emit('showModal', {
        action: 'connection-profile',
        data: {
          connection: connectionData,
        },
      })
    }

    const loadingAnimationOptions = { animationData: loadingAnimation.default }

    const loadingStates = computed(() => {
      return {
        save: saveLoading.value,
        unsave: unsaveLoading.value,
        enable: enableLoading.value,
        disable: disableLoading.value,
      }
    })

    const saveLoading = ref(false)
    const saveConnection = async (connectionId) => {
      saveLoading.value = true
      await store.dispatch('connections/dispatchSaveConnection', {
        connectionId,
        context: props.context,
      })
      saveLoading.value = false
    }

    const unsaveLoading = ref(false)
    const unsaveConnection = async (connectionId) => {
      unsaveLoading.value = true
      await store.dispatch('connections/dispatchUnsaveConnection', {
        connectionId,
        context: props.context,
      })
      unsaveLoading.value = false
    }

    const enableLoading = ref(false)
    const enableConnection = async (connectionId) => {
      enableLoading.value = true
      await store.dispatch('connections/dispatchEnableOrganisationConnection', {
        connectionId,
      })
      enableLoading.value = false
    }

    const disableLoading = ref(false)
    const disableConnection = async (connectionId) => {
      disableLoading.value = true
      await store.dispatch(
        'connections/dispatchDisableOrganisationConnection',
        {
          connectionId,
        },
      )
      disableLoading.value = false
    }

    return {
      actions,
      isOrganisationConnection,
      isSavedConnection,
      canShowAdminMenu,
      showModal,
      saveConnection,
      unsaveConnection,
      loadingStates,
      saveLoading,
      unsaveLoading,
      loadingAnimationOptions,
    }
  },
})
</script>

<style scoped>
@media screen and (max-width: 760px) {
  .min-content-small-screen {
    width: min-content;
  }
}
</style>
